/**
 * Account Context Utilities
 *
 * Holds the per-request account identity (account id + auth token) used for
 * multi-tenant data filtering. A separate module to avoid circular dependencies.
 *
 * The DEFAULT provider is a single module-global context. That is correct for
 * the browser admin app, which is one account per session/process. The API
 * server, however, serves many tenants concurrently in ONE process, so a shared
 * global would let concurrent requests clobber each other's context between
 * `await`s (cross-tenant leakage). The API server therefore installs an
 * AsyncLocalStorage-backed provider at startup via `setAccountContextProvider()`
 * (see backend/api/src/middleware/account-context.ts), giving each request an
 * isolated context. This module stays free of any Node-only imports so it can
 * still be bundled into the browser.
 */

export interface AccountContext {
  accountId: string | null;
  token: string | null;
  // When true, getClient() returns the service-role client (bypasses RLS).
  // Used by trusted, token-validated public endpoints that have no account JWT
  // to present (artist/show/staff/timetable flows) — see the API's
  // runWithServiceRole(). Requires the admin client to be configured.
  serviceRole: boolean;
}

export interface AccountContextProvider {
  get(): AccountContext;
  set(patch: Partial<AccountContext>): void;
}

// Default provider: one shared context (browser / single-tenant-per-process).
const globalContext: AccountContext = { accountId: null, token: null, serviceRole: false };

const defaultProvider: AccountContextProvider = {
  get: () => globalContext,
  set: (patch) => {
    if ('accountId' in patch) globalContext.accountId = patch.accountId ?? null;
    if ('token' in patch) globalContext.token = patch.token ?? null;
    if ('serviceRole' in patch) globalContext.serviceRole = patch.serviceRole ?? false;
  },
};

let provider: AccountContextProvider = defaultProvider;

/**
 * Install a custom account-context provider (e.g. AsyncLocalStorage-backed on
 * the API server). Call once at startup, before any request is handled.
 */
export function setAccountContextProvider(p: AccountContextProvider): void {
  provider = p;
}

/**
 * Set the current account ID for multi-tenant filtering
 */
export function setCurrentAccountId(accountId: string | null): void {
  provider.set({ accountId });
}

/**
 * Get the current account ID
 */
export function getCurrentAccountId(): string | null {
  return provider.get().accountId;
}

/**
 * Set the current account auth token (forwarded to PostgREST once RLS is on)
 */
export function setCurrentAccountToken(token: string | null): void {
  provider.set({ token });
}

/**
 * Get the current account auth token
 */
export function getCurrentAccountToken(): string | null {
  return provider.get().token;
}

/**
 * Prefer the service-role client for this request (bypasses RLS). Only honoured
 * when the admin client is configured; otherwise getClient() falls back to the
 * normal client.
 */
export function setServiceRolePreference(on: boolean): void {
  provider.set({ serviceRole: on });
}

/**
 * Whether the current request prefers the service-role client.
 */
export function getServiceRolePreference(): boolean {
  return provider.get().serviceRole;
}
